import { Col, Row } from "react-bootstrap";
// New package button
import { Button } from '@veeyaainnovatives/button';
import Form from 'react-bootstrap/Form';
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ContactForm = ({ requestType = "quote" }) => {
    const [dragActive, setDragActive] = useState(false);
    const [files, setFiles] = useState([]);
    const [statesData, setStatesData] = useState([]);
    const [servicesData, setServicesData] = useState([]);
    const [projectsData, setProjectsData] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        mobileNumber: "",
        state: "",
        service: "",
        projectType: "",
        budget: "",
        date: "",
        comments: "",
        files: []
    });

    
  const fileInputRef = useRef(null);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const allowedTypes = [
        "image/png", 
        "image/jpeg", 
        "image/jpg", 
        "image/gif",
        "application/pdf",
        "application/msword", // .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" // .docx
    ];

    // Fetch static data from public folder
    useEffect(() => {
        const fetchStaticData = async () => {
            try {
                const [statesRes, servicesRes, projectsRes] = await Promise.all([
                    fetch('/static_datas/state.json'),
                    fetch('/static_datas/service.json'),
                    fetch('/static_datas/project.json')
                ]);
                
                const states = await statesRes.json();
                const services = await servicesRes.json();
                const projects = await projectsRes.json();
                
                setStatesData(states);
                setServicesData(services);
                setProjectsData(projects);
                setDataLoading(false);
            } catch (error) {
                console.error('Error loading static data:', error);
                setDataLoading(false);
            }
        };
        
        fetchStaticData();
    }, []);


    const handleChangeInput = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });

        setErrors({
            ...errors,
            [e.target.name]: ""
        });
    };

    // Handle date change from DatePicker
    const handleDateChange = (date) => {
        if (date) {
            // Format date as dd-mm-yyyy for display
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const formattedDate = `${day}-${month}-${year}`;
            
            // Store in yyyy-mm-dd format for API (ISO format)
            const apiDate = `${year}-${month}-${day}`;
            
            setFormData({
                ...formData,
                date: apiDate
            });
        } else {
            setFormData({
                ...formData,
                date: ""
            });
        }

        setErrors({
            ...errors,
            date: ""
        });
    };

    // Convert date string to Date object for DatePicker
    const getDateValue = () => {
        if (!formData.date) return null;
        // If date is in yyyy-mm-dd format, convert to Date
        const dateParts = formData.date.split('-');
        if (dateParts.length === 3) {
            // Check if it's yyyy-mm-dd format
            if (dateParts[0].length === 4) {
                return new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
            }
            // If it's dd-mm-yyyy format
            return new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
        }
        return null;
    };

    // VALIDATION
    const validateForm = () => {
        let newErrors = {};

        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Enter a valid email";

        if (!formData.mobileNumber.trim()) newErrors.mobileNumber = "Mobile Number is required";
        else if (!/^[0-9]{10}$/.test(formData.mobileNumber))
            newErrors.mobileNumber = "Enter a valid 10-digit number";

        if (!formData.state) newErrors.state = "Select your state";
        if (!formData.service) newErrors.service = "Select a service";
        if (!formData.projectType) newErrors.projectType = "Select a project type";

        setErrors(newErrors);
        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validation = validateForm();
        if (!validation.isValid) {
            const errorFields = Object.keys(validation.errors);
            if (errorFields.length > 0) {
                const fieldNames = errorFields.map(key => {
                    const fieldMap = {
                        name: 'Name',
                        email: 'Email',
                        mobileNumber: 'Mobile Number',
                        state: 'State',
                        service: 'Service',
                        projectType: 'Project Type'
                    };
                    return fieldMap[key] || key;
                });
                toast.error(`Please fill in the required fields: ${fieldNames.join(', ')}`);
            } else {
                toast.error("Please fill in all required fields");
            }
            return;
        }

        setIsSubmitting(true);

        try {
            // Create FormData for file upload
            const formDataToSend = new FormData();
            
            // Add request_type parameter
            formDataToSend.append('request_type', requestType);
            
            // Map form fields to API expected format
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('mobile_number', formData.mobileNumber);
            formDataToSend.append('state', formData.state);
            formDataToSend.append('service', formData.service);
            formDataToSend.append('project_type', formData.projectType);
            
            // Optional fields
            if (formData.budget) {
                formDataToSend.append('budget', formData.budget);
            }
            if (formData.date) {
                formDataToSend.append('project_date', formData.date);
            }
            if (formData.comments) {
                formDataToSend.append('project_details', formData.comments);
            }

            // Append files
            formData.files.forEach((file) => {
                formDataToSend.append('files[]', file);
            });

            // Make API call
            const response = await axios.post(
                getApiUrl('api/public/quote'),
                formDataToSend,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.success) {
                toast.success(response.data.message || "Quote request submitted successfully. We will contact you shortly.");
                
                // Reset form
                setFormData({
                    name: "",
                    email: "",
                    mobileNumber: "",
                    state: "",
                    service: "",
                    projectType: "",
                    budget: "",
                    date: "",
                    comments: "",
                    files: []
                });
                setFiles([]);
                setErrors({});
                
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                toast.error(response.data.message || "Failed to submit quote request");
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            
            // Handle validation errors
            if (error.response && error.response.data && error.response.data.errors) {
                const apiErrors = error.response.data.errors;
                const newErrors = {};
                
                // Map API error keys to form field names
                Object.keys(apiErrors).forEach(key => {
                    if (key === 'mobile_number') {
                        newErrors.mobileNumber = apiErrors[key][0];
                    } else if (key === 'project_type') {
                        newErrors.projectType = apiErrors[key][0];
                    } else if (key.startsWith('files.')) {
                        // File validation errors
                        toast.error(apiErrors[key][0]);
                    } else {
                        newErrors[key] = apiErrors[key][0];
                    }
                });
                
                setErrors(newErrors);
                toast.error(error.response.data.message || "Validation error. Please check the form.");
            } else {
                toast.error(error.response?.data?.message || "Failed to submit quote request. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // File handling remains same --------

    const addFiles = (incomingFiles) => {
        const validFiles = [];
        const invalidFiles = [];
    
        incomingFiles.forEach(file => {
            if (allowedTypes.includes(file.type)) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file.name);
            }
        });
    
        if (invalidFiles.length > 0) {
            toast.error(`Invalid files: ${invalidFiles.join(", ")}. Only PDF, DOC, DOCX, JPEG, JPG, PNG, GIF allowed.`);
        }
    
        // Update both state lists
        setFiles(prev => [...prev, ...validFiles]);
    
        setFormData(prev => ({
            ...prev,
            files: [...prev.files, ...validFiles]   // 🔥 store inside formData
        }));
    };

    
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    
        if (e.dataTransfer.files) {
            addFiles(Array.from(e.dataTransfer.files));
        }
    };
    
    const handleFileInputChange = (e) => {
        if (e.target.files) {
            addFiles(Array.from(e.target.files));
        }
    };
    

    const removeFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        const newFormDataFiles = formData.files.filter((_, i) => i !== index);
    
        setFiles(newFiles);
        setFormData(prev => ({ ...prev, files: newFormDataFiles }));
    };

    const handleDivClick = () => {
        fileInputRef.current.click();
      };

    return (
        <>
            <form noValidate onSubmit={handleSubmit}>

                <Row>
                    {/* NAME */}
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                name="name"
                                placeholder="Your Name"
                                value={formData.name}
                                onChange={handleChangeInput}
                                className={errors.name ? "is-invalid" : ""}
                            />
                            {errors.name && <span className="text-danger ms-3">{errors.name}</span>}
                        </Form.Group>
                    </Col>

                    {/* EMAIL */}
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="email"
                                name="email"
                                placeholder="Your Email"
                                value={formData.email}
                                onChange={handleChangeInput}
                                className={errors.email ? "is-invalid" : ""}
                            />
                            {errors.email && <span className="text-danger ms-3">{errors.email}</span>}
                        </Form.Group>
                    </Col>

                    {/* MOBILE */}
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                name="mobileNumber"
                                placeholder="Mobile Number"
                                value={formData.mobileNumber}
                                onChange={handleChangeInput}
                                className={errors.mobileNumber ? "is-invalid" : ""}
                            />
                            {errors.mobileNumber && <span className="text-danger ms-3">{errors.mobileNumber}</span>}
                        </Form.Group>
                    </Col>

                    {/* STATE */}
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                as="select"
                                name="state"
                                value={formData.state}
                                onChange={handleChangeInput}
                                className={errors.state ? "is-invalid" : ""}
                            >
                                <option value="">Select State</option>
                                {statesData.map((state) => (
                                    <option key={state.id} value={state.id}>
                                        {state.text}
                                    </option>
                                ))}
                            </Form.Control>
                            {errors.state && <span className="text-danger ms-3">{errors.state}</span>}
                        </Form.Group>
                    </Col>

                    {/* SERVICE */}
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                as="select"
                                name="service"
                                value={formData.service}
                                onChange={handleChangeInput}
                                className={errors.service ? "is-invalid" : ""}
                            >
                                <option value="">Select Service</option>
                                {servicesData.map((service) => (
                                    <option key={service.id} value={service.id}>
                                        {service.text}
                                    </option>
                                ))}
                            </Form.Control>
                            {errors.service && <span className="text-danger ms-3">{errors.service}</span>}
                        </Form.Group>
                    </Col>

                    {/* PROJECT TYPE */}
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                as="select"
                                name="projectType"
                                value={formData.projectType}
                                onChange={handleChangeInput}
                                className={errors.projectType ? "is-invalid" : ""}
                            >
                                <option value="">Project Type</option>
                                {projectsData.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.text}
                                    </option>
                                ))}
                            </Form.Control>
                            {errors.projectType && <span className="text-danger ms-3">{errors.projectType}</span>}
                        </Form.Group>
                    </Col>

                    {/* OTHER FIELDS (optional – no validation) */}
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                name="budget"
                                placeholder="Approx. Budget"
                                value={formData.budget}
                                onChange={handleChangeInput}
                            />
                        </Form.Group>
                    </Col>
                    <Col lg={6}> 
                        <Form.Group className="mb-4 position-relative"> 
                            <DatePicker
                                selected={getDateValue()}
                                onChange={handleDateChange}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="DD-MM-YYYY"
                                className="form-control custom-datepicker"
                                wrapperClassName="datepicker-wrapper"
                                isClearable
                                showYearDropdown
                                showMonthDropdown
                                dropdownMode="select"
                                minDate={new Date()}
                            />
                            <span className="datepicker-icon">
                                <i className="fa-regular fa-calendar"></i>
                            </span>
                        </Form.Group> 
                    </Col>
                </Row>

                {/* PROJECT DETAILS */}
                <Form.Group className="mb-3">
                    <Form.Control
                        as="textarea"
                        rows={4}
                        name="comments"
                        placeholder="Project Details"
                        value={formData.comments}
                        onChange={handleChangeInput}
                    />
                </Form.Group>

                {/* FILE UPLOAD */}
                <Form.Group className="mb-4">
                    <div
                        className={`file-upload-drop-zone ${dragActive ? 'active' : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={handleDivClick}    
                    >
                        <h5 className="file-upload-section-title">Upload Project Files / Images</h5>
                        <input
                            type="file"
                            ref={fileInputRef} 
                            id="fileInput"
                            multiple
                            onChange={handleFileInputChange}
                            className="file-input-hidden"
                        />
                        <label className="file-upload-label">
                            <p className="upload-text">Drop or click to browse files</p>
                        </label>
                    </div>
                </Form.Group>

                {files.length > 0 && (
                    <div className="mb-4 uploaded-files-list">
                        <h5 className="uploaded-files-title">Uploaded Files ({files.length})</h5>
                        {files.map((file, i) => (
                            <div key={i} className="uploaded-file-item">
                                <span>{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(i)}
                                    className="remove-file-btn"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <Button 
                    type="submit" 
                    className="btn-block-green h-56px mt-md-3 mb-4 mb-md-0"
                    backgroundColor="#677a58"
                    color="#ffffff"
                    hoverBackgroundColor="#5a6a4a"
                    height="56px"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
            </form>

            {/* Toast */}
            <ToastContainer theme="colored" />
        </>
    );
};

export default ContactForm;
